import React, { useEffect, useState } from 'react'
import connect from './connect'
import SpacingContainerBase from '../../components/layouts/SpacingContainerBase'
import Loader from '../../components/common/Loader'
import { Grid, Box } from '@material-ui/core'
import NothingFoundCard from '../../components/generics/Cards/NothingFoundCard/NothingFoundCardList'
import PaginationSmall from '../../components/common/Paginations/PaginationSmall'
import List from './components/List'
import Header from './components/Header'
import { emptyArray } from '../../utils/arrayUtils'
import { Button, RouterLink } from '../../components/common/Buttons'
import { useTrans } from '../../services/i18n'
import Typography from '../../components/common/Typography'
import { ANY_OF_THOSE, CONTAINS, IS_BETWEEN } from '../../constants/filter'
import { useAlertContext } from '../../contexts/AlertContext'
import { useSelector } from 'react-redux'
import { selectUserPermissions, isEnabled } from '../../selectors'

const SubmittedForms = ({
  submittedForms,
  onInit,
  onChangePage,
  onChangeRowPerPage,
  getStaffUsers,
  onChangeFilters,
  updateFormAdminStatus,
  fetchList,
}) => {
  const { items, count, pending, pagination } = submittedForms
  const { trans } = useTrans()
  const { successAlert, errorAlert } = useAlertContext()
  const [selectedForms, setSelectedForms] = useState([])
  const permissions = useSelector(selectUserPermissions)
  const canEdit = isEnabled(permissions, 'submitted-forms', '', 'edit')

  useEffect(() => {
    getStaffUsers()
  }, [])

  useEffect(() => {
    // Fetch submitted forms on component mount
    onInit()
  }, [onInit])

  const resetFilters = () => {
    onChangeFilters({
      adminStatus: {
        type: ANY_OF_THOSE,
        value: ['new', 'pending', 'closed', 'qualified', 'disqualified'],
      },
      organizationName: {
        type: CONTAINS,
        value: '',
      },
      formName: {
        type: CONTAINS,
        value: '',
      },
      updatedAt: {
        type: IS_BETWEEN,
        value: {
          from: null,
          to: null,
        },
      },
    })
  }

  const handleSelectForm = (formId) => {
    setSelectedForms(prev => {
      if (prev.includes(formId)) {
        return prev.filter(id => id !== formId)
      }
      return [...prev, formId]
    })
  }

  const handleSelectAll = () => {
    if (selectedForms.length === items.length) {
      setSelectedForms([])
    } else {
      setSelectedForms(items.map(item => item.id))
    }
  }

  const handleBatchUpdateStatus = async (adminStatus) => {
    if (selectedForms.length === 0) {
      errorAlert(trans('no-forms-selected'))
      return
    }

    try {
      const forms = selectedForms.map(organizationFormId => ({
        organizationFormId,
        adminStatus
      }))

      const result = await updateFormAdminStatus({ forms })

      if (result.payload.success) {
        successAlert(`${trans('status-updated-successfully')}: ${result.payload.results.success.length} worksheets`)
        setSelectedForms([])
        fetchList()
      }
    } catch (error) {
      errorAlert(trans('error-updating-status'))
    }
  }

  return (
    <SpacingContainerBase>
      <Header />
      {pending && <Loader open={pending} />}
      {!emptyArray(items) ? (
        <Grid container spacing={2} direction="column">
          {canEdit && selectedForms.length > 0 && (
            <Grid item xs zeroMinWidth>
              <Box display="flex" alignItems="center" p={2} style={{ gap: '12px' }}>
                <Typography variant="body1" weight="600" style={{ marginRight: '8px' }}>
                  {trans('selected')}: {selectedForms.length}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleBatchUpdateStatus('new')}
                  style={{
                    textTransform: 'none',
                    padding: '6px 16px',
                    backgroundColor: '#007bff',
                    color: '#fff'
                  }}
                >
                  {trans('mark-as-new')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleBatchUpdateStatus('pending')}
                  style={{
                    textTransform: 'none',
                    padding: '6px 16px',
                    backgroundColor: '#ffac00',
                    color: '#fff'
                  }}
                >
                  {trans('mark-as-pending')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleBatchUpdateStatus('qualified')}
                  style={{
                    textTransform: 'none',
                    padding: '6px 16px',
                    backgroundColor: '#11ca7b',
                    color: '#fff'
                  }}
                >
                  {trans('mark-as-qualified')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleBatchUpdateStatus('disqualified')}
                  style={{
                    textTransform: 'none',
                    padding: '6px 16px',
                    backgroundColor: '#ff493cff',
                    color: '#fff'
                  }}
                >
                  {trans('mark-as-disqualified')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleBatchUpdateStatus('closed')}
                  style={{
                    textTransform: 'none',
                    padding: '6px 16px',
                    backgroundColor: '#00502dff',
                    color: '#fff'
                  }}
                >
                  {trans('mark-as-closed')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedForms([])}
                  style={{ textTransform: 'none', padding: '6px 16px' }}
                >
                  {trans('clear-selection')}
                </Button>
              </Box>
            </Grid>
          )}
          <Grid item xs zeroMinWidth>
            <List
              items={items}
              selectedForms={canEdit ? selectedForms : []}
              onSelectForm={canEdit ? handleSelectForm : undefined}
              onSelectAll={canEdit ? handleSelectAll : undefined}
              canEdit={canEdit}
            />
          </Grid>

          <Grid item xs zeroMinWidth>
            <PaginationSmall
              page={pagination.page}
              perPage={pagination.perPage}
              count={count}
              onChangePage={onChangePage}
              onChangeRowPerPage={onChangeRowPerPage}
              pending={pending}
            />
          </Grid>
        </Grid>
      ) : !pending && emptyArray(items) ? (
        <Grid item xs zeroMinWidth>
          <NothingFoundCard>
            <Button component={RouterLink} onClick={resetFilters}>
              <Typography weight="400" variant="body1">
                {trans('clear-filters')}
              </Typography>
            </Button>
          </NothingFoundCard>
        </Grid>
      ) : null}
    </SpacingContainerBase>
  )
}

export default connect(SubmittedForms)